import React, { createContext, useContext, useState, useEffect } from 'react';

const translations = {
  en: {
    // Navigation
    nav: {
      home: 'Home',
      dashboard: 'Dashboard',
      calculator: 'Calculator',
      history: 'History',
      login: 'Login',
      register: 'Register',
      logout: 'Logout',
    },
    // Landing
    landing: {
      title: 'Analyze All Markets with AI',
      subtitle: 'Upload your TradingView charts and let our AI analyze the best entry points, stop-loss, and take-profit levels. Crypto, Forex, Stocks, Indices, Commodities.',
      cta: 'Get Started',
      feature1Title: 'Multi-Market Analysis',
      feature1Desc: 'AI analyzes your charts to identify optimal trade setups on any market',
      feature2Title: 'Risk Management',
      feature2Desc: 'Automatic position sizing based on your risk tolerance',
      feature3Title: 'Trade Journal',
      feature3Desc: 'Track and analyze your trading performance',
    },
    // Auth
    auth: {
      loginTitle: 'Welcome Back',
      loginSubtitle: 'Sign in to access your trading dashboard',
      registerTitle: 'Create Account',
      registerSubtitle: 'Start making smarter trading decisions',
      email: 'Email',
      password: 'Password',
      name: 'Full Name',
      loginBtn: 'Sign In',
      registerBtn: 'Create Account',
      noAccount: "Don't have an account?",
      hasAccount: 'Already have an account?',
    },
    // Dashboard
    dashboard: {
      title: 'Trading Dashboard',
      uploadTitle: 'Upload Chart',
      uploadDesc: 'Drop your TradingView screenshot here or click to upload',
      analyzing: 'Analyzing chart...',
      symbol: 'Symbol',
      timeframe: 'Timeframe',
      capital: 'Capital ($)',
      risk: 'Risk (%)',
      analyzeBtn: 'Analyze Chart',
      resultTitle: 'AI Analysis',
      direction: 'Direction',
      confidence: 'Confidence',
      entry: 'Entry Price',
      stopLoss: 'Stop Loss',
      tp1: 'Take Profit 1',
      tp2: 'Take Profit 2',
      tp100: 'Take Profit 100%',
      riskReward: 'Risk/Reward',
      positionSize: 'Position Size',
      positionValue: 'Position Value',
      saveTradeBtn: 'Save Trade',
      tradeSaved: 'Trade saved successfully',
    },
    // Calculator
    calculator: {
      title: 'Position Calculator',
      subtitle: 'Calculate your optimal position size',
      capital: 'Total Capital',
      riskPercent: 'Risk per Trade (%)',
      entryPrice: 'Entry Price',
      stopLoss: 'Stop Loss',
      calculate: 'Calculate',
      results: 'Results',
      positionUnits: 'Position Size (Units)',
      positionValue: 'Position Value ($)',
      riskPerUnit: 'Risk per Unit ($)',
      maxLoss: 'Maximum Loss ($)',
    },
    // History
    history: {
      title: 'Trade History',
      noTrades: 'No trades yet. Start by analyzing a chart!',
      symbol: 'Symbol',
      direction: 'Direction',
      entry: 'Entry',
      sl: 'SL',
      tp: 'TP',
      rr: 'R:R',
      status: 'Status',
      date: 'Date',
      actions: 'Actions',
      statusOpen: 'OPEN',
      statusWin: 'WIN',
      statusLoss: 'LOSS',
      delete: 'Delete',
    },
    // Common
    common: {
      long: 'LONG',
      short: 'SHORT',
      loading: 'Loading...',
      error: 'An error occurred',
      success: 'Success',
    },
  },
  fr: {
    // Navigation
    nav: {
      home: 'Accueil',
      dashboard: 'Tableau de bord',
      calculator: 'Calculateur',
      history: 'Historique',
      login: 'Connexion',
      register: "S'inscrire",
      logout: 'Déconnexion',
    },
    // Landing
    landing: {
      title: 'Analysez tous les marchés avec l\'IA',
      subtitle: 'Uploadez vos graphiques TradingView et laissez notre IA analyser les meilleurs points d\'entrée, stop-loss et take-profit. Crypto, Forex, Actions, Indices, Matières Premières.',
      cta: 'Commencer',
      feature1Title: 'Analyse Multi-Marchés',
      feature1Desc: 'L\'IA analyse vos graphiques pour identifier les meilleures configurations sur tous les marchés',
      feature2Title: 'Gestion du Risque',
      feature2Desc: 'Calcul automatique de la taille de position selon votre tolérance au risque',
      feature3Title: 'Journal de Trading',
      feature3Desc: 'Suivez et analysez vos performances de trading',
    },
    // Auth
    auth: {
      loginTitle: 'Bon Retour',
      loginSubtitle: 'Connectez-vous pour accéder à votre tableau de bord',
      registerTitle: 'Créer un Compte',
      registerSubtitle: 'Commencez à prendre des décisions de trading plus intelligentes',
      email: 'Email',
      password: 'Mot de passe',
      name: 'Nom complet',
      loginBtn: 'Se Connecter',
      registerBtn: 'Créer un Compte',
      noAccount: "Pas encore de compte ?",
      hasAccount: 'Déjà un compte ?',
    },
    // Dashboard
    dashboard: {
      title: 'Tableau de Bord',
      uploadTitle: 'Uploader un Graphique',
      uploadDesc: 'Déposez votre capture TradingView ici ou cliquez pour uploader',
      analyzing: 'Analyse en cours...',
      symbol: 'Symbole',
      timeframe: 'Unité de temps',
      capital: 'Capital ($)',
      risk: 'Risque (%)',
      analyzeBtn: 'Analyser le Graphique',
      resultTitle: 'Analyse IA',
      direction: 'Direction',
      confidence: 'Confiance',
      entry: 'Prix d\'Entrée',
      stopLoss: 'Stop Loss',
      tp1: 'Take Profit 1',
      tp2: 'Take Profit 2',
      tp100: 'Take Profit 100%',
      riskReward: 'Risque/Gain',
      positionSize: 'Taille de Position',
      positionValue: 'Valeur de Position',
      saveTradeBtn: 'Sauvegarder le Trade',
      tradeSaved: 'Trade sauvegardé avec succès',
    },
    // Calculator
    calculator: {
      title: 'Calculateur de Position',
      subtitle: 'Calculez votre taille de position optimale',
      capital: 'Capital Total',
      riskPercent: 'Risque par Trade (%)',
      entryPrice: 'Prix d\'Entrée',
      stopLoss: 'Stop Loss',
      calculate: 'Calculer',
      results: 'Résultats',
      positionUnits: 'Taille Position (Unités)',
      positionValue: 'Valeur Position ($)',
      riskPerUnit: 'Risque par Unité ($)',
      maxLoss: 'Perte Maximum ($)',
    },
    // History
    history: {
      title: 'Historique des Trades',
      noTrades: 'Aucun trade encore. Commencez par analyser un graphique !',
      symbol: 'Symbole',
      direction: 'Direction',
      entry: 'Entrée',
      sl: 'SL',
      tp: 'TP',
      rr: 'R:R',
      status: 'Statut',
      date: 'Date',
      actions: 'Actions',
      statusOpen: 'OUVERT',
      statusWin: 'GAGNÉ',
      statusLoss: 'PERDU',
      delete: 'Supprimer',
    },
    // Common
    common: {
      long: 'LONG',
      short: 'SHORT',
      loading: 'Chargement...',
      error: 'Une erreur est survenue',
      success: 'Succès',
    },
  },
};

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('language');
    return saved || 'fr';
  });

  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  };

  const toggleLanguage = () => {
    setLanguage((prev) => (prev === 'en' ? 'fr' : 'en'));
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
